import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getApiErrorMessage } from "@/lib/api";
import {
  candidateAccess,
  candidateSaveAnswer,
  candidateSubmit,
  type CandidateFicha,
  type CandidateQuestion,
  type CandidateStage,
} from "@/lib/selectionApi";

const CODE_KEY = "selecaoCandidatoCodigo";

function parseOptions(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function parseTable(raw: string | null): { colunas: string[]; linhas: string[][] } | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.tabela && Array.isArray(parsed.tabela.colunas)) return parsed.tabela;
    return null;
  } catch {
    return null;
  }
}

function formatTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

type Values = Record<string, { text: string; json: string | null }>;

function buildValues(ficha: CandidateFicha): Values {
  const v: Values = {};
  for (const stage of ficha.stages) {
    for (const q of stage.questions) {
      v[q.id] = { text: q.valueText ?? "", json: q.valueJson ?? null };
    }
  }
  return v;
}

export default function CandidatePortal() {
  const [code, setCode] = useState<string>("");
  const [inputCode, setInputCode] = useState<string>("");
  const [ficha, setFicha] = useState<CandidateFicha | null>(null);
  const [values, setValues] = useState<Values>({});
  const [loading, setLoading] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeStage, setActiveStage] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const locked = ficha != null && ficha.status !== "EM_PREENCHIMENTO";

  const loadFromFicha = useCallback((f: CandidateFicha) => {
    setFicha(f);
    setValues(buildValues(f));
    setSavedAt(f.savedAt);
  }, []);

  // Restore a session: a code from the shared link (?codigo=) takes priority, else the stored code.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (params.get("codigo") ?? params.get("code") ?? "").trim().toUpperCase();
    const stored = window.localStorage.getItem(CODE_KEY);
    const initial = fromUrl || stored;
    if (!initial) return;
    if (fromUrl) setInputCode(fromUrl);
    setLoading(true);
    candidateAccess(initial)
      .then((f) => { setCode(initial); window.localStorage.setItem(CODE_KEY, initial); loadFromFicha(f); })
      .catch(() => { if (!fromUrl) window.localStorage.removeItem(CODE_KEY); })
      .finally(() => setLoading(false));
  }, [loadFromFicha]);

  const enter = async () => {
    const c = inputCode.trim().toUpperCase();
    if (!c) return;
    setLoading(true);
    try {
      const f = await candidateAccess(c);
      setCode(c);
      window.localStorage.setItem(CODE_KEY, c);
      loadFromFicha(f);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Código de acesso inválido"));
    } finally {
      setLoading(false);
    }
  };

  const exit = () => {
    window.localStorage.removeItem(CODE_KEY);
    setCode("");
    setInputCode("");
    setFicha(null);
    setValues({});
  };

  const persist = useCallback(async (questionId: string, text: string, json: string | null) => {
    if (!code) return;
    setSaving(true);
    try {
      const res = await candidateSaveAnswer(code, questionId, text.length ? text : null, json);
      setSavedAt(res.savedAt);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Falha ao salvar"));
    } finally {
      setSaving(false);
    }
  }, [code]);

  const scheduleSave = useCallback((questionId: string, text: string, json: string | null) => {
    if (timers.current[questionId]) clearTimeout(timers.current[questionId]);
    timers.current[questionId] = setTimeout(() => { void persist(questionId, text, json); }, 700);
  }, [persist]);

  const setText = (q: CandidateQuestion, text: string) => {
    setValues((prev) => ({ ...prev, [q.id]: { text, json: prev[q.id]?.json ?? null } }));
    scheduleSave(q.id, text, values[q.id]?.json ?? null);
  };

  const toggleMulti = (q: CandidateQuestion, option: string) => {
    const current = (() => {
      try { const a = JSON.parse(values[q.id]?.json ?? "[]"); return Array.isArray(a) ? a.map(String) : []; }
      catch { return []; }
    })();
    const next = current.includes(option) ? current.filter((o) => o !== option) : [...current, option];
    const json = JSON.stringify(next);
    setValues((prev) => ({ ...prev, [q.id]: { text: prev[q.id]?.text ?? "", json } }));
    scheduleSave(q.id, values[q.id]?.text ?? "", json);
  };

  const setSingle = (q: CandidateQuestion, option: string) => {
    setValues((prev) => ({ ...prev, [q.id]: { text: option, json: null } }));
    scheduleSave(q.id, option, null);
  };

  // Refresh completeness (✓ marks) from the server after edits settle.
  const refresh = useCallback(async () => {
    if (!code) return;
    try { const f = await candidateAccess(code); setFicha(f); setSavedAt(f.savedAt); } catch { /* ignore */ }
  }, [code]);

  const stages = ficha?.stages ?? [];
  const reviewIndex = stages.length; // virtual "Revisar e enviar" step
  const completedCount = useMemo(() => stages.filter((s) => s.complete).length, [stages]);

  const submit = async () => {
    if (!code) return;
    setSubmitting(true);
    try {
      const f = await candidateSubmit(code);
      loadFromFicha(f);
      toast.success("Ficha enviada com sucesso");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível enviar a ficha"));
    } finally {
      setSubmitting(false);
    }
  };

  // ---- render: access screen ----
  if (!ficha) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm rounded-xl border border-border bg-surface-elevated p-6 shadow-sm">
          <div className="text-xs font-bold uppercase tracking-wider text-primary">NATUREXPRESS</div>
          <h1 className="mt-1 text-lg font-semibold">Processo Seletivo</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Digite o código de acesso de 6 caracteres que você recebeu.
          </p>
          <Input
            className="mt-4 text-center text-lg tracking-[0.3em] uppercase"
            maxLength={6}
            value={inputCode}
            onChange={(e) => setInputCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") void enter(); }}
            placeholder="ABC234"
            autoFocus
          />
          <Button className="mt-4 w-full" onClick={() => void enter()} disabled={loading || inputCode.trim().length < 6}>
            {loading ? "Entrando…" : "Entrar"}
          </Button>
        </div>
      </div>
    );
  }

  const onReview = activeStage >= reviewIndex;
  const stage: CandidateStage | undefined = stages[activeStage];
  const totalSteps = stages.length + 1; // etapas visíveis ao candidato + "Revisar e enviar"
  const currentStep = onReview ? totalSteps : activeStage + 1;
  const progressPct = totalSteps > 0 ? Math.round((currentStep / totalSteps) * 100) : 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b border-border bg-surface-elevated/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{ficha.processName}</div>
            <div className="truncate text-xs text-muted-foreground">Olá, {ficha.candidateName || "candidato"}</div>
          </div>
          <div className="ml-auto text-xs text-muted-foreground">
            {saving ? "Salvando…" : savedAt ? `Salvo às ${formatTime(savedAt)}` : ""}
          </div>
          <Button variant="ghost" size="sm" onClick={exit}>Sair</Button>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-4">
        {/* Identificação do processo */}
        <div className="mb-4 rounded-xl border border-border bg-surface-elevated p-4">
          <h1 className="text-lg font-bold leading-tight">Processo Seletivo</h1>
          <p className="font-serif text-base text-foreground">{ficha.processName}</p>
          <dl className="mt-3 grid gap-1 text-sm">
            <div className="flex gap-2"><dt className="text-muted-foreground">Empresa:</dt><dd>NatureXpress</dd></div>
            <div className="flex gap-2"><dt className="text-muted-foreground">Processo seletivo:</dt><dd>{ficha.processName}</dd></div>
            <div className="flex gap-2"><dt className="text-muted-foreground">Candidato:</dt><dd>{ficha.candidateName || "—"}</dd></div>
          </dl>
          <div className="mt-3 rounded-lg bg-secondary/40 p-3">
            <p className="text-sm font-medium">Olá, {ficha.candidateName || "candidato"}!</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Você está participando deste processo seletivo. Responda às etapas com atenção. Suas respostas serão salvas automaticamente.
            </p>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Etapa {currentStep} de {totalSteps}</span>
              <span>{progressPct}%</span>
            </div>
            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        </div>

        {/* stage rail */}
        <div className="mb-4 flex flex-wrap gap-2">
          {stages.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => { void refresh(); setActiveStage(i); }}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${
                i === activeStage ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
              }`}
            >
              <span>{i + 1}. {s.title}</span>
              {s.complete && <span aria-label="completa">✓</span>}
            </button>
          ))}
          <button
            type="button"
            onClick={() => { void refresh(); setActiveStage(reviewIndex); }}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${
              onReview ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
            }`}
          >
            {stages.length + 1}. Revisar e enviar
          </button>
        </div>

        {locked && (
          <div className="mb-4 rounded-lg border border-border bg-secondary/40 p-4 text-sm">
            Sua ficha foi enviada. As respostas não podem mais ser editadas.
          </div>
        )}

        {!onReview && stage && (
          <div className="rounded-xl border border-border bg-surface-elevated p-4">
            <h2 className="text-base font-semibold">{stage.title}</h2>
            <StageTable meta={stage.meta} />
            <div className="mt-4 flex flex-col gap-5">
              {stage.questions.map((q) => (
                <QuestionField
                  key={q.id}
                  q={q}
                  value={values[q.id] ?? { text: "", json: null }}
                  disabled={locked}
                  onText={(t) => setText(q, t)}
                  onToggleMulti={(o) => toggleMulti(q, o)}
                  onSingle={(o) => setSingle(q, o)}
                />
              ))}
            </div>
            <div className="mt-6 flex justify-between">
              <Button variant="outline" disabled={activeStage === 0}
                onClick={() => { void refresh(); setActiveStage((i) => Math.max(0, i - 1)); }}>
                Voltar
              </Button>
              <Button onClick={() => { void refresh(); setActiveStage((i) => i + 1); }}>Continuar</Button>
            </div>
          </div>
        )}

        {onReview && (
          <div className="rounded-xl border border-border bg-surface-elevated p-4">
            <h2 className="text-base font-semibold">Revisar e enviar</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {completedCount} de {stages.length} etapas completas.
            </p>
            <ul className="mt-4 flex flex-col gap-2">
              {stages.map((s, i) => (
                <li key={s.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                  <span>{i + 1}. {s.title}</span>
                  <button type="button" className="text-xs underline text-muted-foreground"
                    onClick={() => setActiveStage(i)}>
                    {s.complete ? "✓ completa" : "incompleta — revisar"}
                  </button>
                </li>
              ))}
            </ul>
            {!locked && (
              <Button
                className="mt-6 w-full"
                disabled={!ficha.canSubmit || submitting}
                onClick={() => {
                  if (window.confirm("Enviar a ficha? Após o envio, as respostas não poderão ser alteradas.")) void submit();
                }}
              >
                {submitting ? "Enviando…" : ficha.canSubmit ? "Enviar ficha" : "Preencha os campos obrigatórios"}
              </Button>
            )}
          </div>
        )}
        <footer className="mt-6 border-t border-border pt-4 text-center text-xs text-muted-foreground">
          Em caso de dúvidas, entre em contato com a equipe responsável pelo processo seletivo da NatureXpress.
        </footer>
      </div>
    </div>
  );
}

function StageTable({ meta }: { meta: string | null }) {
  const table = parseTable(meta);
  if (!table) return null;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full border-collapse text-sm font-mono">
        <thead>
          <tr>
            {table.colunas.map((c) => (
              <th key={c} className="border border-border bg-secondary/40 px-2 py-1 text-left">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.linhas.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} className="border border-border px-2 py-1">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QuestionField({
  q, value, disabled, onText, onToggleMulti, onSingle,
}: {
  q: CandidateQuestion;
  value: { text: string; json: string | null };
  disabled: boolean;
  onText: (t: string) => void;
  onToggleMulti: (o: string) => void;
  onSingle: (o: string) => void;
}) {
  const options = parseOptions(q.options);
  const selected = (() => {
    try { const a = JSON.parse(value.json ?? "[]"); return Array.isArray(a) ? a.map(String) : []; }
    catch { return []; }
  })();
  return (
    <div>
      <label className="block font-serif text-[15px] leading-snug">
        {q.statement}{q.required && <span className="text-destructive"> *</span>}
      </label>
      {q.helpText && <p className="mt-1 text-xs text-muted-foreground whitespace-pre-line">{q.helpText}</p>}
      <div className="mt-2">
        {q.answerType === "SHORT_TEXT" && (
          <Input value={value.text} disabled={disabled} onChange={(e) => onText(e.target.value)} />
        )}
        {q.answerType === "LONG_TEXT" && (
          <Textarea rows={4} value={value.text} disabled={disabled} onChange={(e) => onText(e.target.value)} />
        )}
        {q.answerType === "MULTI_CHOICE" && (
          <div className="flex flex-wrap gap-2">
            {options.map((o) => (
              <button key={o} type="button" disabled={disabled} onClick={() => onToggleMulti(o)}
                className={`rounded-md border px-3 py-1.5 text-sm ${
                  selected.includes(o) ? "border-primary bg-primary/10 text-primary" : "border-border"
                }`}>
                {o}
              </button>
            ))}
          </div>
        )}
        {(q.answerType === "SINGLE_CHOICE" || q.answerType === "YES_NO") && (
          <div className="flex flex-wrap gap-2">
            {(q.answerType === "YES_NO" ? ["Sim", "Não"] : options).map((o) => (
              <button key={o} type="button" disabled={disabled} onClick={() => onSingle(o)}
                className={`rounded-md border px-3 py-1.5 text-sm ${
                  value.text === o ? "border-primary bg-primary/10 text-primary" : "border-border"
                }`}>
                {o}
              </button>
            ))}
          </div>
        )}
        {q.answerType === "SCORE" && (
          <Input type="number" min={1} max={5} value={value.text} disabled={disabled}
            onChange={(e) => onText(e.target.value)} />
        )}
      </div>
    </div>
  );
}
